import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  LocalHospital as HospitalIcon,
  Bloodtype as BloodIcon,
  Warning as AllergyIcon,
  Medication as MedIcon,
  LocalPharmacy as PharmacyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';

interface MedicalProfileForm {
  blood_type: string;
  allergies: string[];
  chronic_conditions: string[];
  current_medications: string[];
  emergency_notes: string;
  primary_doctor_name?: string;
  primary_doctor_phone?: string;
  medical_center?: string;
  insurance_provider?: string;
  insurance_number?: string;
}

const steps = ['Información Básica', 'Alergias y Condiciones', 'Medicamentos', 'Contactos Médicos'];

// Common medical data for Chilean context
const bloodTypes = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const commonAllergies = [
  'Penicilina', 'Aspirina', 'Ibuprofeno', 'Mariscos', 'Nueces', 'Huevos', 
  'Leche', 'Soja', 'Trigo', 'Polen', 'Ácaros', 'Latex', 'Contraste yodado'
];

const commonConditions = [
  'Hipertensión arterial', 'Diabetes tipo 1', 'Diabetes tipo 2', 'Asma', 
  'Artritis', 'Osteoporosis', 'Hipotiroidismo', 'Hipertiroidismo', 
  'Enfermedad cardíaca', 'Colesterol alto', 'Ansiedad', 'Depresión',
  'Migraña', 'Epilepsia', 'EPOC'
];

const commonMedications = [
  'Losartán', 'Enalapril', 'Amlodipino', 'Metformina', 'Insulina',
  'Levotiroxina', 'Atorvastatina', 'Simvastatina', 'Aspirina',
  'Omeprazol', 'Salbutamol', 'Prednisolona', 'Clonazepam', 'Sertralina'
];

const insuranceProviders = [
  'FONASA', 'ISAPRE Banmédica', 'ISAPRE Colmena', 'ISAPRE Cruz Blanca',
  'ISAPRE Vida Tres', 'ISAPRE Consalud', 'ISAPRE Nueva Masvida', 'Otro'
];

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useForm<MedicalProfileForm>({
    defaultValues: {
      allergies: [],
      chronic_conditions: [],
      current_medications: [],
    }
  });

  const watchedAllergies = watch('allergies');
  const watchedConditions = watch('chronic_conditions');
  const watchedMedications = watch('current_medications');

  const addItem = (field: 'allergies' | 'chronic_conditions' | 'current_medications', value: string) => {
    const currentValues = getValues(field);
    if (value && !currentValues.includes(value)) {
      setValue(field, [...currentValues, value]);
    }
  };

  const removeItem = (field: 'allergies' | 'chronic_conditions' | 'current_medications', index: number) => {
    const currentValues = getValues(field);
    setValue(field, currentValues.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const onSubmit = async (data: MedicalProfileForm) => {
    setIsLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/api/v1/users/medical-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Error al guardar perfil médico');
      }

      // Navigate to the main patient dashboard
      navigate('/', { replace: true });

    } catch (error: any) {
      setError(error.message || 'Error al guardar perfil médico');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Información Médica Básica
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Esta información es crucial para brindarte la mejor atención en caso de emergencia.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.blood_type}>
                <InputLabel>Tipo de Sangre</InputLabel>
                <Select
                  label="Tipo de Sangre"
                  {...register('blood_type', { required: 'El tipo de sangre es requerido' })}
                >
                  {bloodTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas de Emergencia"
                multiline
                rows={3}
                placeholder="Información importante que el personal médico debería saber en una emergencia..."
                helperText="Ejemplo: Alérgico a penicilina, portador de marcapasos, etc."
                {...register('emergency_notes')}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Tu tipo de sangre será visible para el personal médico de emergencia. 
                  Si no conoces tu tipo de sangre, puedes dejarlo en blanco y completarlo más tarde.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Alergias y Condiciones Médicas
              </Typography>
            </Grid>

            {/* Allergies */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <AllergyIcon sx={{ mr: 1 }} />
                  Alergias
                </Typography>
                
                <Controller
                  name="allergies"
                  control={control}
                  render={() => (
                    <Autocomplete
                      multiple
                      options={commonAllergies}
                      value={watchedAllergies}
                      onChange={(_, newValue) => setValue('allergies', newValue)}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Escribe o selecciona alergias"
                          helperText="Presiona Enter para agregar una alergia personalizada"
                        />
                      )}
                    />
                  )}
                />
              </Paper>
            </Grid>

            {/* Chronic Conditions */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <HospitalIcon sx={{ mr: 1 }} />
                  Condiciones Médicas Crónicas
                </Typography>
                
                <Controller
                  name="chronic_conditions"
                  control={control}
                  render={() => (
                    <Autocomplete
                      multiple
                      options={commonConditions}
                      value={watchedConditions}
                      onChange={(_, newValue) => setValue('chronic_conditions', newValue)}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Escribe o selecciona condiciones médicas"
                          helperText="Incluye cualquier enfermedad crónica o condición médica importante"
                        />
                      )}
                    />
                  )}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="body2">
                  <strong>Importante:</strong> Esta información será accesible para el personal médico 
                  en situaciones de emergencia. Asegúrate de incluir toda información relevante.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Medicamentos Actuales
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <MedIcon sx={{ mr: 1 }} />
                  Medicamentos que tomas regularmente
                </Typography>
                
                <Controller
                  name="current_medications"
                  control={control}
                  render={() => (
                    <Autocomplete
                      multiple
                      options={commonMedications}
                      value={watchedMedications}
                      onChange={(_, newValue) => setValue('current_medications', newValue)}
                      freeSolo
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            variant="outlined"
                            label={option}
                            {...getTagProps({ index })}
                            key={index}
                            icon={<PharmacyIcon />}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Escribe o selecciona medicamentos"
                          helperText="Incluye medicamentos con receta, vitaminas y suplementos"
                        />
                      )}
                    />
                  )}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  Incluye todos los medicamentos que tomas regularmente, incluyendo:
                  <br />• Medicamentos con receta médica
                  <br />• Medicamentos de venta libre
                  <br />• Vitaminas y suplementos
                  <br />• Medicamentos homeopáticos o naturales
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Contactos e Información Médica
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre del Médico Principal"
                placeholder="Dr. Juan Pérez"
                {...register('primary_doctor_name')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono del Médico"
                placeholder="+56 9 1234 5678"
                {...register('primary_doctor_phone')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Centro Médico Principal"
                placeholder="Hospital Regional, Clínica Las Condes, etc."
                {...register('medical_center')}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Previsión de Salud</InputLabel>
                <Select
                  label="Previsión de Salud"
                  {...register('insurance_provider')}
                >
                  {insuranceProviders.map((provider) => (
                    <MenuItem key={provider} value={provider}>
                      {provider}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Afiliado"
                placeholder="123456789"
                {...register('insurance_number')}
              />
            </Grid>

            <Grid item xs={12}>
              <Alert severity="success">
                <Typography variant="body2">
                  <strong>¡Perfil casi completo!</strong><br />
                  Tu información médica será encriptada y solo accesible por personal médico autorizado en emergencias.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Card sx={{ maxWidth: 800, width: '100%', m: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
            <HospitalIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" fontWeight="bold">
                Perfil Médico
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completa tu información médica para emergencias
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)}>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
              >
                Atrás
              </Button>
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isLoading}
                  sx={{ minWidth: 120 }}
                  startIcon={<CheckIcon />}
                >
                  {isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Completar Perfil'
                  )}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                >
                  Siguiente
                </Button>
              )}
            </Box>
          </form>

          <Box mt={4} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              Puedes modificar esta información en cualquier momento desde tu perfil.
            </Typography>
          </Box>

          <Box mt={2} textAlign="center">
            <Button
              variant="text"
              size="small"
              onClick={() => navigate('/', { replace: true })}
            >
              Completar más tarde
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Onboarding;